import { gql, useLazyQuery } from '@apollo/client';
import { ERROR_TYPE, TTError, handleException } from 'models/ErrorHandling';
import { useContext } from 'react';
import { UserAuthContext } from 'contexts/UserAuthContext';

import { enqueueSnackbar } from 'notistack';
import { AuthenticatedItemModel } from 'pages/authentication/models/AuthenticatedItemModel';
import { LessonModel, LessonVersionModel } from './LessonModel';

const QUERY_GQL = gql`
    query GetSlideQuery($lessonID: ID!) {
        authenticatedItem {
            ... on User {
                id
                name
                email
                role
                studentProfile {
                    id
                    user {
                        id
                    }
                    studentNumber
                    familyName
                    givenName
                    dob
                }
            }
        }
        lesson(where: { id: $lessonID }) {
            id
            latestLessonDraft {
                id
                lessonName
                slides(orderBy: { sort: asc }) {
                    id
                    sort
                    data
                    questionCount
                }
                published
            }
            shareWithPublic
            teacher {
                user {
                    id
                }
            }
        }
    }
`;

type QueryVariableType = {
    lessonID: string;
};

type ModelQueryType = {
    authenticatedItem: AuthenticatedItemModel;
    lesson: LessonModel;
};

export const useLessonQuery = () => {
    const auth = useContext(UserAuthContext);
    const [queryData, { data, loading }] = useLazyQuery<ModelQueryType>(QUERY_GQL);

    const getLesson = async (variables: QueryVariableType): Promise<ModelQueryType | null> => {
        try {
            const result = await queryData({
                variables,
                fetchPolicy: 'no-cache',
                notifyOnNetworkStatusChange: true,
            });

            if (!result?.data) {
                throw new TTError(ERROR_TYPE.NO_DATA);
            }

            return result?.data;
        } catch (err) {
            const errorMsg = handleException(err);
            if (errorMsg === ERROR_TYPE.KS_ACCESS_DENIED) {
                auth.accessDenied(errorMsg);
            } else {
                enqueueSnackbar(errorMsg, {
                    variant: 'error',
                    anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
                });
            }
            throw new TTError();
        }
        return null;
    };

    return { getLesson, data, loading };
};
